import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { DashboardPeriod } from "@/utils/dashboardDateUtils";

interface DashboardPeriodBarProps {
  period: DashboardPeriod;
  label: string;
  offset: number;
  isRefreshing?: boolean;
  onPeriodChange: (period: DashboardPeriod) => void;
  onOffsetChange: (offset: number) => void;
}

const PERIODS: DashboardPeriod[] = ["week", "month", "year"];

export function DashboardPeriodBar({
  period,
  label,
  offset,
  isRefreshing = false,
  onPeriodChange,
  onOffsetChange,
}: DashboardPeriodBarProps) {
  const { t } = useTranslation("dashboard");

  return (
    <div className="dashboard-period-bar sticky top-0 z-20 -mx-3 border-y border-border/70 bg-background/95 px-3 py-2 backdrop-blur supports-[backdrop-filter]:bg-background/85 sm:mx-0 sm:rounded-2xl sm:border sm:px-4">
      <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="grid grid-cols-3 rounded-xl bg-muted/70 p-1" aria-label={t("period.ariaLabel")}>
          {PERIODS.map((item) => (
            <Button
              key={item}
              type="button"
              variant="ghost"
              size="sm"
              className={cn(
                "h-10 min-w-0 rounded-lg px-2 text-xs sm:px-4 sm:text-sm",
                period === item && "bg-card text-foreground shadow-sm hover:bg-card hover:text-foreground",
              )}
              aria-pressed={period === item}
              onClick={() => onPeriodChange(item)}
            >
              {t(`period.${item}`)}
            </Button>
          ))}
        </div>

        <div className="grid min-w-0 grid-cols-[44px_minmax(0,1fr)_44px] items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-11 w-11 rounded-xl"
            onClick={() => onOffsetChange(offset + 1)}
            aria-label={t("period.previous")}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="flex min-w-0 items-center justify-center gap-2 px-1 text-center">
            <span className="truncate text-xs font-semibold tabular-nums sm:text-sm">{label}</span>
            {isRefreshing && (
              <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-primary motion-reduce:animate-none" aria-label={t("period.refreshing")} />
            )}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-11 w-11 rounded-xl"
            onClick={() => onOffsetChange(Math.max(0, offset - 1))}
            disabled={offset === 0}
            aria-label={t("period.next")}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}